import React, { useRef, useState, useEffect, useCallback } from 'react';
import { F1, SMap } from '@client/new/helpers/types';
import * as styles from './Gallery.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';

export type GalleryMediaType = 'video' | 'photo';
export type GalleryMedia = {
  type: GalleryMediaType;
  thumbnailURL: string;
  url: string;
  width?: number;
  height?: number;
};

export type GalleryProps = {
  media: Array<GalleryMedia>;
  onChange: F1<number>;
  selected: number;
  fullWidth?: boolean;
  showButtons?: boolean;
};

const DRAG_THRESHOLD = 5;
const VISIBILITY_THRESHOLD = 0.2;
type Direction = 'left' | 'right';

export const Gallery = (p: GalleryProps) => {
  const { media, onChange, selected, fullWidth } = p;
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);
  const [widths, setWidths] = useState<SMap<string>>({});
  const [dragDirection, setDragDirection] = useState<Direction>('right');

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    setIsDragging(true);
    setHasMoved(false);

    setStartX(e.pageX - containerRef.current.offsetLeft);
    setStartY(e.pageY - containerRef.current.offsetTop);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();

    const distX = Math.abs(e.pageX - containerRef.current.offsetLeft - startX);
    const distY = Math.abs(e.pageY - containerRef.current.offsetTop - startY);
    const distance = Math.sqrt(distX ** 2 + distY ** 2);

    if (distance > DRAG_THRESHOLD) setHasMoved(true);

    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - startX;

    setDragDirection(walk > 0 ? 'left' : 'right');

    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const centerThumbnail = useCallback(
    (index: number) => {
      if (!containerRef.current) return;
      const containerEl = containerRef.current;
      const itemEl = itemRefs.current[index];
      if (!containerEl || !itemEl) return;

      const containerRect = containerEl.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();

      const containerCenter = containerRect.left + containerRect.width / 2;
      const itemCenter = itemRect.left + itemRect.width / 2;
      const scrollOffset = itemCenter - containerCenter;

      const left = containerEl.scrollLeft + scrollOffset;
      const behavior =
        fullWidth && Math.abs(scrollOffset) > containerRect.width * 2
          ? 'instant'
          : 'smooth';

      containerEl.scrollTo({ left, behavior });
    },
    [fullWidth],
  );

  const handleClickImage = useCallback(
    (index: number) => {
      if (hasMoved) return;
      onChange(index);
      centerThumbnail(index);
    },
    [hasMoved, onChange, centerThumbnail],
  );

  const isItemAtLeastXPercentVisible = useCallback(
    (p: {
      index: number;
      containerRect: DOMRect;
      direction: 'left' | 'right';
      threshold: number;
    }) => {
      const item = itemRefs.current[p.index];
      if (!item) return false;

      const itemRect = item.getBoundingClientRect();
      const visibleWidth =
        p.direction === 'right'
          ? p.containerRect.right - itemRect.left
          : itemRect.right - p.containerRect.left;
      return visibleWidth >= itemRect.width * p.threshold;
    },
    [],
  );

  const endDrag = useCallback(
    (threshold?: number) => {
      setIsDragging(false);

      if (!fullWidth || !containerRef.current || !hasMoved) return;

      const containerEl = containerRef.current;
      const containerRect = containerEl.getBoundingClientRect();

      const currentIndex = selected;
      let targetIndex = currentIndex;
      const nextIndex = currentIndex + 1;
      const prevIndex = currentIndex - 1;

      if (
        dragDirection === 'right' &&
        nextIndex < media.length &&
        isItemAtLeastXPercentVisible({
          index: nextIndex,
          containerRect,
          direction: 'right',
          threshold: threshold || VISIBILITY_THRESHOLD,
        })
      )
        targetIndex = nextIndex;
      else if (
        dragDirection === 'left' &&
        prevIndex >= 0 &&
        isItemAtLeastXPercentVisible({
          index: prevIndex,
          containerRect,
          direction: 'left',
          threshold: threshold || VISIBILITY_THRESHOLD,
        })
      )
        targetIndex = prevIndex;

      if (targetIndex !== selected) onChange(targetIndex);
      else centerThumbnail(selected);
    },
    [
      fullWidth,
      hasMoved,
      selected,
      dragDirection,
      media.length,
      onChange,
      centerThumbnail,
      isItemAtLeastXPercentVisible,
    ],
  );

  const onMouseUp = useCallback(() => {
    endDrag();
  }, [endDrag]);

  const onWrapperMouseLeave = useCallback(() => {
    if (isDragging) endDrag(0.03);
  }, [isDragging, endDrag]);

  useEffect(() => {
    centerThumbnail(selected);
  }, [selected, centerThumbnail]);

  useEffect(() => {
    const nextWidths = media.reduce(
      (acc, i) => ({ ...acc, [i.url]: getWidth(i) }),
      {} as SMap<string>,
    );
    setWidths(nextWidths);
  }, [containerRef.current, media]);

  const canGoPrev = selected > 0;
  const canGoNext = selected < media.length - 1;

  const onPrev = () => (canGoPrev ? onChange(selected - 1) : null);
  const onNext = () => (canGoNext ? onChange(selected + 1) : null);
  const getWidth = (v: GalleryMedia) => {
    if (!containerRef.current?.clientHeight || !v.height || !v.width)
      return '100%';
    return `${(containerRef.current.clientHeight * v.width) / v.height}px`;
  };

  return (
    <div
      className={styles.wrapper2}
      onMouseLeave={onWrapperMouseLeave}
      style={p.fullWidth ? {} : { justifyContent: 'center' }}
    >
      <div
        ref={containerRef}
        className={styles.wrapper}
        onMouseDown={handleMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        {media.map((item, i) => {
          const isSelected = i === selected;
          let imageClass = fullWidth ? styles.image : styles.thumbnail;
          const width = widths[item.url] || '100%';
          if (isSelected)
            imageClass = fullWidth
              ? styles.selectedImage
              : styles.selectedThumbnail;

          return (
            <div
              key={i}
              ref={(el) => (itemRefs.current[i] = el)}
              onDragStart={(e) => e.preventDefault()}
              className={imageClass}
              onClick={() => handleClickImage(i)}
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {item.type === 'video' ? (
                <video
                  src={item.url}
                  className={imageClass}
                  muted
                  controls={p.fullWidth}
                  autoPlay={false}
                  style={{ height: '100%', width: 'auto' }}
                  controlsList="nofullscreen"
                />
              ) : (
                <img
                  src={p.fullWidth ? item.url : item.thumbnailURL}
                  className={imageClass}
                  style={p.fullWidth ? { width } : {}}
                />
              )}
            </div>
          );
        })}
      </div>
      {p.showButtons ? (
        <>
          <div
            className={styles.buttonLeft}
            onClick={onPrev}
            style={canGoPrev ? {} : { opacity: 0, cursor: 'auto' }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </div>

          <div
            className={styles.buttonRight}
            onClick={onNext}
            style={canGoNext ? {} : { opacity: 0, cursor: 'auto' }}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </>
      ) : null}
    </div>
  );
};
